import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  Chip,
  useTheme,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MoneyIcon,
  BarChart as ChartIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  HelpOutline as HelpOutlineIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { Metric, metricGroupedCategories } from '@features/deals/types';

interface DealFinancialsTablesProps {
  metrics: Metric[];
  years: number[];
}

const DealFinancialsTables: React.FC<DealFinancialsTablesProps> = ({ metrics, years }) => {
  const theme = useTheme();

  // Helper function to format currency
  const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numValue)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  // Helper function to format percentage
  const formatPercent = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numValue)) return '-';
    return `${numValue.toFixed(1)}%`;
  };

  // Filter metrics by category
  const getMetricsByCategory = (category: string): Metric[] => {
    const categoryTerms = category.toLowerCase().split(/[,\s]+/);

    // Log what we're searching for to help debug
    console.log(`Searching for metrics matching category: ${category}`, categoryTerms);

    const results = metrics.filter(metric => {
      // Try to match against multiple terms (more flexible)
      return categoryTerms.some(term => {
        if (!term) return false;

        // Direct category match (case insensitive)
        if (metric.category?.toLowerCase() === term) return true;

        // Category contains the search term
        if (metric.category?.toLowerCase().includes(term)) return true;

        // Item contains the search term
        if (metric.item?.toLowerCase()?.includes(term)) return true;

        return false;
      });
    });

    // Log what we found to help debug
    console.log(`Found ${results.length} metrics for ${category}:`, results);

    return results;
  };

  // Get KPI metrics (occupancy, ADR, RevPAR)
  const kpiMetrics = useMemo(() => {
    // More flexible category matching for common KPI terms
    const occupancyMetrics = getMetricsByCategory('occupancy');
    const adrMetrics = getMetricsByCategory('adr average daily rate');
    const revparMetrics = getMetricsByCategory('revpar revenue per available room');

    // Add secondary search if primary search returns no results
    const findMetricsWithFallback = (primaryMetrics: Metric[], fallbackTerms: string[]): Metric[] => {
      if (primaryMetrics.length > 0) {
        return primaryMetrics;
      }

      // Try fallback search directly on the metrics array
      console.log(`Trying fallback search for terms:`, fallbackTerms);
      return metrics.filter(m =>
        fallbackTerms.some(term =>
          m.category?.toLowerCase().includes(term) ||
          m.item?.toLowerCase()?.includes(term)
        )
      );
    };

    const finalOccupancyMetrics = findMetricsWithFallback(occupancyMetrics, ['occupancy', 'occ rate', 'occ %']);
    const finalAdrMetrics = findMetricsWithFallback(adrMetrics, ['adr', 'average daily', 'daily rate']);
    const finalRevparMetrics = findMetricsWithFallback(revparMetrics, ['revpar', 'rev par', 'revenue per']);

    console.log('Final KPI metrics after fallback:');
    console.log('- Occupancy:', finalOccupancyMetrics);
    console.log('- ADR:', finalAdrMetrics);
    console.log('- RevPAR:', finalRevparMetrics);

    // Remove duplicates by keeping only one metric per year
    const uniqueByYear = (metrics: Metric[]): Metric[] => {
      const uniqueMetrics: { [year: number]: Metric } = {};
      metrics.forEach(metric => {
        // If we haven't seen this year yet or this one is more specific
        if (!uniqueMetrics[metric.year] ||
            (metric.item && !uniqueMetrics[metric.year].item)) {
          uniqueMetrics[metric.year] = metric;
        }
      });
      return Object.values(uniqueMetrics);
    };

    return {
      occupancy: uniqueByYear(finalOccupancyMetrics),
      adr: uniqueByYear(finalAdrMetrics),
      revpar: uniqueByYear(finalRevparMetrics)
    };
  }, [metrics, getMetricsByCategory]);

  // Get P&L metrics
  const plMetrics = useMemo(() => {
    // More flexible category matching for financial terms
    const revenueMetrics = getMetricsByCategory('revenue income');
    const expenseMetrics = getMetricsByCategory('expense expenses cost costs');
    const noiMetrics = getMetricsByCategory('noi net operating income');

    // Add secondary search if primary search returns no results
    const findMetricsWithFallback = (primaryMetrics: Metric[], fallbackTerms: string[]): Metric[] => {
      if (primaryMetrics.length > 0) {
        return primaryMetrics;
      }

      // Try fallback search directly on the metrics array
      console.log(`Trying fallback search for terms:`, fallbackTerms);
      return metrics.filter(m =>
        fallbackTerms.some(term =>
          m.category?.toLowerCase().includes(term) ||
          m.item?.toLowerCase()?.includes(term)
        )
      );
    };

    const finalRevenueMetrics = findMetricsWithFallback(revenueMetrics, ['revenue', 'income', 'sales', 'total revenue']);
    const finalExpenseMetrics = findMetricsWithFallback(expenseMetrics, ['expense', 'expenses', 'cost', 'costs', 'total expense']);
    const finalNoiMetrics = findMetricsWithFallback(noiMetrics, ['noi', 'net operating', 'operating income', 'profit']);

    console.log('Final P&L metrics after fallback:');
    console.log('- Revenue:', finalRevenueMetrics);
    console.log('- Expenses:', finalExpenseMetrics);
    console.log('- NOI:', finalNoiMetrics);

    // Group metrics by item name to avoid duplicates
    const groupByItem = (metrics: Metric[]): Metric[] => {
      const groupedMetrics: { [key: string]: Metric } = {};

      metrics.forEach(metric => {
        const itemKey = `${metric.item || 'total'}-${metric.year}`;
        if (!groupedMetrics[itemKey] || metric.category.toLowerCase().includes('total')) {
          groupedMetrics[itemKey] = metric;
        }
      });

      return Object.values(groupedMetrics);
    };

    return {
      revenue: groupByItem(finalRevenueMetrics),
      expense: groupByItem(finalExpenseMetrics),
      noi: groupByItem(finalNoiMetrics)
    };
  }, [metrics, getMetricsByCategory]);

  // Get unique items for display
  const uniqueItems = useMemo(() => {
    return {
      revenue: Array.from(new Set(plMetrics.revenue.map(m => m.item || 'Total Revenue'))),
      expense: Array.from(new Set(plMetrics.expense.map(m => m.item || 'Total Expenses'))),
      noi: Array.from(new Set(plMetrics.noi.map(m => m.item || 'Net Operating Income')))
    };
  }, [plMetrics]);

  // Add debugging for the metrics prop
  useEffect(() => {
    console.log('DealFinancialsTables received metrics:', metrics);
    console.log('Years to display:', years);

    // Check for potential data issues
    if (metrics.length === 0) {
      console.warn('No metrics data provided to DealFinancialsTables');
    }

    // Analyze the structure of metrics to help debugging
    const categories = Array.from(new Set(metrics.map(m => m.category)));
    console.log('Available metric categories:', categories);

    const occupancyMetrics = metrics.filter(m =>
      m.category?.toLowerCase().includes('occupancy') ||
      (m.item?.toLowerCase()?.includes('occupancy'))
    );

    const revenueMetrics = metrics.filter(m =>
      m.category?.toLowerCase().includes('revenue') ||
      (m.item?.toLowerCase()?.includes('revenue'))
    );

    const expenseMetrics = metrics.filter(m =>
      m.category?.toLowerCase().includes('expense') ||
      m.category?.toLowerCase().includes('expenses') ||
      (m.item?.toLowerCase()?.includes('expense')) ||
      (m.item?.toLowerCase()?.includes('expenses'))
    );

    console.log('Direct filtering results:');
    console.log('- Occupancy metrics:', occupancyMetrics);
    console.log('- Revenue metrics:', revenueMetrics);
    console.log('- Expense metrics:', expenseMetrics);
  }, [metrics, years]);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* KPI Metrics Table Section */}
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SpeedIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight="600">
                Key Performance Indicators
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {kpiMetrics.occupancy.length > 0 || kpiMetrics.adr.length > 0 || kpiMetrics.revpar.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Metric</TableCell>
                      {years.map(year => (
                        <TableCell key={year} align="right" sx={{ color: '#fff', fontWeight: 600 }}>
                          {year}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={500}>Occupancy Rate</Typography>
                          <Chip size="small" label="%" sx={{ ml: 1, height: 20 }} />
                        </Box>
                      </TableCell>
                      {years.map(year => {
                        const metric = kpiMetrics.occupancy.find(m => m.year === year);
                        return (
                          <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                            {metric ? formatPercent(metric.value) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={500}>Average Daily Rate (ADR)</Typography>
                          <Chip size="small" label="$" sx={{ ml: 1, height: 20 }} />
                        </Box>
                      </TableCell>
                      {years.map(year => {
                        const metric = kpiMetrics.adr.find(m => m.year === year);
                        return (
                          <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                            {metric ? formatCurrency(metric.value) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={500}>Revenue Per Available Room (RevPAR)</Typography>
                          <Chip size="small" label="$" sx={{ ml: 1, height: 20 }} />
                        </Box>
                      </TableCell>
                      {years.map(year => {
                        const metric = kpiMetrics.revpar.find(m => m.year === year);
                        return (
                          <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                            {metric ? formatCurrency(metric.value) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No KPI data available. Please add Occupancy, ADR or RevPAR metrics in the Data Entry tab.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Revenue & Expenses Table Section */}
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.success.main}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MoneyIcon sx={{ mr: 1, color: theme.palette.success.main }} />
              <Typography variant="h6" fontWeight="600">
                Profit & Loss Statement
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {plMetrics.revenue.length > 0 || plMetrics.expense.length > 0 || plMetrics.noi.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.success.main }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Item</TableCell>
                      {years.map(year => (
                        <TableCell key={year} align="right" sx={{ color: '#fff', fontWeight: 600 }}>
                          {year}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Revenue Section */}
                    <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                      <TableCell colSpan={2 + years.length} sx={{ fontWeight: 600 }}>
                        Revenue
                      </TableCell>
                    </TableRow>
                    {uniqueItems.revenue.length > 0 ? (
                      uniqueItems.revenue.map((item, index) => (
                        <TableRow key={`revenue-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                          <TableCell>Revenue</TableCell>
                          <TableCell>{item}</TableCell>
                          {years.map(year => (
                            <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                              {(() => {
                                const yearMetric = plMetrics.revenue.find(m => m.year === year && (m.item === item || (!m.item && item === 'Total Revenue')));
                                return yearMetric ? formatCurrency(yearMetric.value) : '-';
                              })()}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2 + years.length} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No revenue data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Expenses Section */}
                    <TableRow sx={{ backgroundColor: '#ffebee' }}>
                      <TableCell colSpan={2 + years.length} sx={{ fontWeight: 600 }}>
                        Expenses
                      </TableCell>
                    </TableRow>
                    {uniqueItems.expense.length > 0 ? (
                      uniqueItems.expense.map((item, index) => (
                        <TableRow key={`expense-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                          <TableCell>Expenses</TableCell>
                          <TableCell>{item}</TableCell>
                          {years.map(year => (
                            <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                              {(() => {
                                const yearMetric = plMetrics.expense.find(m => m.year === year && (m.item === item || (!m.item && item === 'Total Expenses')));
                                return yearMetric ? formatCurrency(yearMetric.value) : '-';
                              })()}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2 + years.length} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No expense data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* NOI Section */}
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell colSpan={2 + years.length} sx={{ fontWeight: 600 }}>
                        Net Operating Income
                      </TableCell>
                    </TableRow>
                    {uniqueItems.noi.length > 0 ? (
                      uniqueItems.noi.map((item, index) => (
                        <TableRow key={`noi-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                          <TableCell>NOI</TableCell>
                          <TableCell>{item}</TableCell>
                          {years.map(year => (
                            <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 600, color: theme.palette.success.main }}>
                              {(() => {
                                const yearMetric = plMetrics.noi.find(m => m.year === year && (m.item === item || (!m.item && item === 'Net Operating Income')));
                                return yearMetric ? formatCurrency(yearMetric.value) : '-';
                              })()}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2 + years.length} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No NOI data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No profit & loss data available. Please add Revenue, Expense or NOI metrics in the Data Entry tab.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Financial Performance Indicators Section */}
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.info.main}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ChartIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              <Typography variant="h6" fontWeight="600">
                Financial Performance Indicators
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.info.main }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Performance Indicator</TableCell>
                    {years.map(year => (
                      <TableCell key={year} align="right" sx={{ color: '#fff', fontWeight: 600 }}>
                        {year}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>Profit Margin</Typography>
                    </TableCell>
                    {years.map(year => {
                      const revMetric = plMetrics.revenue.find(m => m.year === year && (!m.item || m.item.toLowerCase().includes('total')));
                      const noiMetric = plMetrics.noi.find(m => m.year === year && (!m.item || m.item.toLowerCase().includes('net')));

                      let profitMargin = null;
                      if (revMetric && noiMetric) {
                        const rev = typeof revMetric.value === 'string' ? Number(revMetric.value.replace(/[^0-9.-]/g, '')) : revMetric.value;
                        const noi = typeof noiMetric.value === 'string' ? Number(noiMetric.value.replace(/[^0-9.-]/g, '')) : noiMetric.value;

                        if (!isNaN(rev) && !isNaN(noi) && rev > 0) {
                          profitMargin = (noi / rev) * 100;
                        }
                      }

                      return (
                        <TableCell key={year} align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                          {profitMargin !== null ? formatPercent(profitMargin) : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>Revenue Growth</Typography>
                    </TableCell>
                    {years.map((year, index) => {
                      const currentRevMetric = plMetrics.revenue.find(m => m.year === year && (!m.item || m.item.toLowerCase().includes('total')));
                      const prevYear = index > 0 ? years[index - 1] : null;
                      const prevRevMetric = prevYear ? plMetrics.revenue.find(m => m.year === prevYear && (!m.item || m.item.toLowerCase().includes('total'))) : null;

                      let growth = null;
                      if (currentRevMetric && prevRevMetric) {
                        const currentRev = typeof currentRevMetric.value === 'string' ? Number(currentRevMetric.value.replace(/[^0-9.-]/g, '')) : currentRevMetric.value;
                        const prevRev = typeof prevRevMetric.value === 'string' ? Number(prevRevMetric.value.replace(/[^0-9.-]/g, '')) : prevRevMetric.value;

                        if (!isNaN(currentRev) && !isNaN(prevRev) && prevRev > 0) {
                          growth = ((currentRev - prevRev) / prevRev) * 100;
                        }
                      }

                      return (
                        <TableCell key={year} align="right" sx={{
                          fontFamily: '"Roboto Mono", monospace',
                          color: growth !== null && growth > 0
                            ? theme.palette.success.main
                            : growth !== null && growth < 0
                              ? theme.palette.error.main
                              : 'inherit'
                        }}>
                          {growth !== null ? formatPercent(growth) : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Last Resort Fallback: Display all metrics if nothing is shown above */}
        {kpiMetrics.occupancy.length === 0 &&
         kpiMetrics.adr.length === 0 &&
         kpiMetrics.revpar.length === 0 &&
         plMetrics.revenue.length === 0 &&
         plMetrics.expense.length === 0 &&
         plMetrics.noi.length === 0 &&
         metrics.length > 0 && (
          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                borderLeft: `4px solid ${theme.palette.warning.main}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600" color="warning.main">
                  All Available Metrics (Fallback View)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The system couldn't categorize your metrics into standard tables.
                Here's a raw view of all available data:
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.warning.main }}>
                      <TableCell sx={{ color: '#fff' }}>Category</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Item</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Year</TableCell>
                      <TableCell sx={{ color: '#fff' }} align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.map((metric, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell>{metric.category}</TableCell>
                        <TableCell>{metric.item || '-'}</TableCell>
                        <TableCell>{metric.year}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                          {typeof metric.value === 'number' ||
                           (typeof metric.value === 'string' && !isNaN(Number(metric.value.replace(/[^0-9.-]/g, ''))))
                            ? formatCurrency(metric.value)
                            : String(metric.value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Debug section for troubleshooting */}
        <Grid item xs={12}>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<BugReportIcon />}
              onClick={() => {
                console.log('DEBUG: Metrics passed to DealFinancialsTables:', metrics);

                // Create a better formatted diagnostic report
                const diagnostic = {
                  metricsCount: metrics.length,
                  years,
                  categories: Array.from(new Set(metrics.map(m => m.category))),
                  categoryCounts: metrics.reduce((acc, metric) => {
                    acc[metric.category] = (acc[metric.category] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>),
                  sampleMetrics: metrics.slice(0, 3),
                  kpiMetrics: {
                    occupancy: kpiMetrics.occupancy.length,
                    adr: kpiMetrics.adr.length,
                    revpar: kpiMetrics.revpar.length
                  },
                  plMetrics: {
                    revenue: plMetrics.revenue.length,
                    expense: plMetrics.expense.length,
                    noi: plMetrics.noi.length
                  }
                };

                console.log('Diagnostic Report:', diagnostic);

                // Show an alert with diagnostic info that users can copy
                alert(
                  `Diagnostic Report:\n` +
                  `Total metrics: ${diagnostic.metricsCount}\n` +
                  `Years to display: ${years.join(', ')}\n` +
                  `Categories found: ${diagnostic.categories.join(', ')}\n\n` +
                  `If tables are not showing correctly, please copy this report and send it to support.`
                );
              }}
              sx={{ color: 'text.secondary' }}
            >
              Troubleshoot Tables
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DealFinancialsTables;